import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  RefObject,
} from 'react';
import { TFunction } from 'i18next';
import {
  Image,
  Paperclip,
  Smiley,
  VideoCamera,
  XCircle,
} from '@thinkific/chalk/react-icons';
import {
  Button,
  NotificationInline,
  Text,
} from '@thinkific/chalk/react-components';
import EmojiPicker from 'components/EmojiPicker/EmojiPicker';
import { AttachmentPreview } from 'components/AttachmentPreview/AttachmentPreview';
import AttachmentInput from 'components/AttachmentInput/AttachmentInput';
import MentionsTextArea from 'components/Mentions/MentionsTextArea';
import { ACCEPTED_FILE_TYPES, BREAKPOINT_MD } from 'utils/constants';
import { Control, Controller, FieldErrors, useWatch } from 'react-hook-form';
import AttachmentVideoInput from 'components/AttachmentVideoInput';
import { Attachment } from 'schema/Post/types';
import { validateFiles } from 'utils/fileHelper';
import { PostAttachmentType } from '__generated__/graphql/legacy/graphql';
import useFileOperations from 'hooks/useFileOperations';
import { insertEmoji } from 'utils/mentions';
import { FormValues as ReplyEditorFormValues } from 'components/ReplyEditorContext/ReplyEditorContext';
import { FormValues as PostFormValues } from 'components/PostForm/PostForm';
import { useWindowSize } from 'hooks/useWindowSize';
import { useCommunityContext } from 'components/CommunityContext';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import styles from './RichTextEditor.module.scss';
import {
  actionButtons,
  attachmentContainer,
  contentContainer,
  iconsContainer,
  replyTextContainer,
  richTextEditorContainer,
  toolbar,
} from './RichTextEditor.css';

type AllFormValues = PostFormValues | ReplyEditorFormValues;

export interface RichTextEditorHandle {
  reset: () => void;
}

interface RichTextEditorProps {
  t: TFunction;
  isEditing?: boolean;
  showTitle?: boolean;
  cancelEdit?: () => void;
  control: Control<AllFormValues>;
  errors: FieldErrors<AllFormValues>;
  setValue: (name: string, value: unknown) => void;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  contentPlaceholder?: string;
  replyPlaceholder?: string;
  closeReplyOnComment?: () => void;
  mentionsTextAreaRef: RefObject<HTMLTextAreaElement | HTMLInputElement>;
}

const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  (
    {
      t,
      isEditing = false,
      showTitle = false,
      cancelEdit,
      control,
      errors,
      setValue,
      onSubmit,
      isSubmitting,
      contentPlaceholder,
      replyPlaceholder,
      closeReplyOnComment,
      mentionsTextAreaRef,
    }: RichTextEditorProps,
    ref
  ) => {
    const { trackEvent } = useCommunityContext();
    const [videoInputOpen, setVideoInputOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isTitleFocused, setIsTitleFocused] = useState(false);
    const attachments = useWatch({ control, name: 'attachments' });

    const { width } = useWindowSize();
    const isMobile = width < BREAKPOINT_MD;

    const { isUploading, uploadFiles, generatePreviews } = useFileOperations();

    useImperativeHandle(ref, () => ({
      reset: () => {
        setVideoInputOpen(false);
        setError(null);
      },
    }));

    useEffect(() => {
      for (const key in errors) {
        if (errors[key]?.message) {
          setError(errors[key].message as string);
        }
      }
    }, [errors]);

    useEffect(() => {
      // If we're editing and there's a video attachment, open the video input
      if (
        isEditing &&
        attachments?.some(
          (att) => att.attachmentType === PostAttachmentType.Video
        )
      ) {
        setVideoInputOpen(true);
      }
    }, [isEditing, attachments]);

    const onFileSelected = async (files: File[]) => {
      const currentAttachments = control._formValues.attachments;
      setError(null);
      const validationError = validateFiles(
        files,
        currentAttachments?.length || 0
      );
      if (validationError) {
        setError(validationError);
        return;
      }

      const previews = generatePreviews(files);

      setValue('attachments', [...currentAttachments, ...previews]);

      const filesToUpload = files.map((file, index) => ({
        file,
        tempId: previews[index].tempId,
      }));

      const {
        attachments: uploadedAttachments,
        error: uploadError,
      } = await uploadFiles(filesToUpload);

      if (uploadError) {
        setError(uploadError);
      } else {
        // Replace the previews with the permanent attachments
        const existingAttachments = control._formValues.attachments;
        const updatedAttachments = existingAttachments.map((att) => {
          const found = uploadedAttachments.find(
            (a) => a.tempId === att.tempId
          );
          if (found) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { tempId, ...rest } = found;
            return rest;
          }
          return att;
        });
        setValue('attachments', updatedAttachments);
      }
    };

    const removeAttachment = (index: number) => {
      setValue(
        'attachments',
        attachments.filter((_, i) => i !== index)
      );
    };

    const onVideoSelected = (attachment: Attachment) => {
      setValue('attachments', [...attachments, attachment]);
    };

    const closeVideoInput = () => {
      // remove video from attachments if it exists
      setValue(
        'attachments',
        attachments.filter(
          (attachment) => attachment.attachmentType !== PostAttachmentType.Video
        )
      );
      setVideoInputOpen(false);
    };

    const handleMentionChange = (value, onChange, mentions) => {
      onChange(value);

      const mentionedIds = mentions?.map((mention) => mention?.id) || [];
      setValue('mentionedUserIds', mentionedIds);
    };

    const handleEmojiPick = (emoji: string) => {
      const inputElement = mentionsTextAreaRef.current;
      if (!inputElement) return;
      const { selectionStart, value: inputValue } = inputElement;
      const contentValue = control._formValues.content || '';
      const updatedContent = insertEmoji(
        emoji,
        selectionStart,
        inputValue,
        contentValue
      );
      setValue('content', updatedContent);
      inputElement.focus();
    };

    const updateAltText = (altText: string, index: number) => {
      const currentAttachments = control._formValues.attachments;
      setValue(
        'attachments',
        currentAttachments.map((attachment, i) =>
          i === index ? { ...attachment, altText } : attachment
        )
      );
    };

    const sendAttachmentTrackEvent = (type: string) => {
      trackEvent(TrackingEvent.ATTACHMENT_CLICKED, {
        [TrackingProperty.ATTACHMENT]: type,
      });
    };

    return (
      <div className={replyPlaceholder ? styles.replyBorderStyle : ''}>
        {replyPlaceholder && (
          <div className={replyTextContainer}>
            <div>
              <Text variant="label.xs">
                {isEditing
                  ? t('components-richTextEditor-editing', 'Editing')
                  : t(
                      'components-richTextEditor-reply_to_post',
                      'Replying to '
                    )}
              </Text>
              <Text variant="body.xs">&nbsp;{replyPlaceholder}</Text>
            </div>
            {!isEditing && (
              <Button
                className={styles.cancelReplyButton}
                emphasis="low"
                icon={XCircle}
                iconStyle="filled"
                size="sm"
                variant="default"
                hiddenLabel
                onClick={closeReplyOnComment}
              >
                {t('components-richTextEditor-cancel', 'Cancel')}
              </Button>
            )}
          </div>
        )}
        <div className={richTextEditorContainer}>
          <div className={contentContainer}>
            {showTitle && (
              <Controller
                control={control}
                name="title"
                render={({ field }) => (
                  <input
                    className={styles.titleContainer}
                    data-qa="post-form__title"
                    id="post-form__title"
                    placeholder="Post title"
                    type="text"
                    value={field.value || ''}
                    onBlur={() => setIsTitleFocused(false)}
                    onChange={field.onChange}
                    onFocus={() => setIsTitleFocused(true)}
                  />
                )}
              />
            )}
            {error && (
              <NotificationInline
                a11yImportance="assertive"
                actions={[]}
                description={error}
                title="Error"
                variant="danger"
                dismissable
                onDismiss={() => setError(null)}
              />
            )}
            <Controller
              control={control}
              name="content"
              render={({ field }) => (
                <MentionsTextArea
                  forwardRef={mentionsTextAreaRef}
                  placeholder={
                    contentPlaceholder ||
                    t('components-homeFeed-write_a_post_placeholder_text')
                  }
                  relevantMentions={[]}
                  textAreaClassName="mentions__text-area"
                  value={field.value || ''}
                  onChange={(_event, newValue, _newPlaintextValue, mentions) =>
                    handleMentionChange(newValue, field.onChange, mentions)
                  }
                />
              )}
            />
            {videoInputOpen && (
              <AttachmentVideoInput
                attachmentUrl={
                  attachments.find(
                    (att) => att.attachmentType === PostAttachmentType.Video
                  )?.url
                }
                onChange={onVideoSelected}
                onClose={closeVideoInput}
              />
            )}
            {attachments.length > 0 && (
              <div className={attachmentContainer}>
                {attachments.map((attachment, index) => (
                  <AttachmentPreview
                    attachment={attachment}
                    attachmentType={attachment.attachmentType}
                    key={attachment.id}
                    t={t}
                    onAltTextChange={(altText: string) =>
                      updateAltText(altText, index)
                    }
                    onRemove={() => removeAttachment(index)}
                  />
                ))}
              </div>
            )}
          </div>
          <div className={toolbar}>
            <div className={iconsContainer}>
              {[Image, Paperclip].map((Icon) => (
                <AttachmentInput
                  accept={ACCEPTED_FILE_TYPES}
                  ariaControls="attachment-file-picker__input"
                  icon={Icon}
                  key={Icon.displayName}
                  multiple
                  onBtnClicked={() => {
                    sendAttachmentTrackEvent(
                      Icon.displayName === 'Image' ? 'image' : 'file'
                    );
                  }}
                  onFileSelected={onFileSelected}
                />
              ))}
              <Button
                data-qa="rich-text-editor__video-button"
                disabled={videoInputOpen}
                emphasis="low"
                icon={VideoCamera}
                size="sm"
                hiddenLabel
                onClick={() => {
                  sendAttachmentTrackEvent('video');
                  setVideoInputOpen(true);
                }}
              >
                Video
              </Button>
              {!isMobile && (
                <EmojiPicker
                  disabled={isTitleFocused}
                  icon={Smiley}
                  t={t}
                  onPick={handleEmojiPick}
                  onTrackClick={() => {
                    sendAttachmentTrackEvent('emoji');
                  }}
                />
              )}
            </div>
            <div className={actionButtons}>
              {isEditing && (
                <Button
                  size="sm"
                  type="button"
                  variant="default"
                  onClick={cancelEdit}
                >
                  {t('Cancel', 'Cancel')}
                </Button>
              )}
              <Button
                dataQa="post-form__submit"
                disabled={isUploading}
                isLoading={isSubmitting}
                size="sm"
                type="submit"
                variant="primary"
                onClick={onSubmit}
              >
                {isEditing
                  ? t('components-richTextEditor-save', 'Save')
                  : t('components-richTextEditor-post', 'Post')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
